import React, { useEffect, useRef } from 'react';
import CONSTANTS from 'src/utils/constants';
import TPEAction from 'src/models/common/TPEAction';
import ServiceCollection from 'src/services/ServiceCollection';
import { ExecutionStatus } from 'src/models/multidimensional-allocation/ExecutionStatus';
import { ACTIONS } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderReducer';
import {
    EXECUTE_FAILED_MESSAGE, EXECUTE_SUCCEEDED_MESSAGE, parseDasExecuteMessage, warningsMessage,
} from 'src/services/derived-allocation-sheet/dasExecution';

/** How often a run in progress is checked, in case its last status update is lost with the connection. */
export const EXECUTION_POLL_INTERVAL_MS = 15000;

/**
 * Executes a derived allocation sheet's draft over the ATP WebSocket, as the TPA worksheet's Execute does, and
 * follows the run: its status updates, then a banner with the outcome and its warnings, and a reload of the sheet
 * and its steps. A run already in progress when the draft opens (from a reload or another tab) is picked up, and a
 * run in progress is also checked through its execution status, so it still ends when the connection drops.
 * @returns execute, which starts a run of the draft.
 */
export default function useDasExecution(args: {
    services: ServiceCollection,
    sheetId: string,
    /** True when the open version is the draft, the only version Execute runs. */
    isDraft: boolean,
    executing: boolean,
    dasDispatch: React.Dispatch<TPEAction>,
}): { execute: () => void } {
    const { services, sheetId, isDraft, executing, dasDispatch } = args;
    const executingRef = useRef(false);
    // The sheet this page started a run on, until the run ends; status updates carry no sheet ID.
    const startedSheetRef = useRef<string | null>(null);
    // The sheet of the last run started here: the route's answer, with the warnings, can come after the last update.
    const lastStartedSheetRef = useRef<string | null>(null);
    // The run whose warnings were shown: the last status update and the route's answer can both carry them.
    const warnedExecutionRef = useRef<string | null>(null);

    const setExecuting = (value: boolean) => {
        executingRef.current = value;
        dasDispatch(ACTIONS.SET_EXECUTING.withPayload(value));
    };

    /** Ends the run once, whichever of the WebSocket and the status check reports it first. */
    const finish = (succeeded: boolean, reason?: string) => {
        if (!executingRef.current) return;
        executingRef.current = false;
        startedSheetRef.current = null;
        if (succeeded) {
            services.messageService.showSuccessBanner(EXECUTE_SUCCEEDED_MESSAGE);
        } else {
            services.messageService.showErrorBanner(reason || EXECUTE_FAILED_MESSAGE);
        }
        dasDispatch(ACTIONS.EXECUTION_FINISHED);
    };

    useEffect(() => {
        const listener = (raw: string) => {
            const parsed = parseDasExecuteMessage(raw);
            if (!parsed) return;
            if (parsed.kind === 'status') {
                if (startedSheetRef.current !== sheetId || parsed.status === CONSTANTS.PROCESSING_STATUS.IN_PROGRESS) return;
                finish(parsed.status === CONSTANTS.PROCESSING_STATUS.SUCCEEDED, parsed.message);
                return;
            }
            if (lastStartedSheetRef.current !== sheetId) return;
            const { execution } = parsed;
            if (startedSheetRef.current === sheetId) {
                finish(execution.status === CONSTANTS.PROCESSING_STATUS.SUCCEEDED, execution.reason);
            }
            const warnings = warningsMessage(execution.warnings);
            if (warnings && warnedExecutionRef.current !== execution.executionId) {
                warnedExecutionRef.current = execution.executionId;
                services.messageService.showWarningBanner(warnings);
            }
        };
        services.atpWebSocketApiService.addListener(listener);
        return () => services.atpWebSocketApiService.removeListener('', listener);
    }, [sheetId]);

    useEffect(() => {
        // Another sheet or version is open: forget the previous one's run, then catch up with this draft's.
        startedSheetRef.current = null;
        if (executingRef.current) setExecuting(false);
        if (!isDraft) return undefined;
        let active = true;
        services.derivedAllocationSheetService.getDerivedAllocationSheetExecutionStatus({ sheetId })
            .then(({ executionStatus }) => {
                if (active && executionStatus === ExecutionStatus.IN_PROGRESS && !executingRef.current) setExecuting(true);
            })
            .catch(() => {
                // Best effort: without the status, a run started elsewhere shows once it ends and the sheet is reread.
            });
        return () => { active = false; };
    }, [sheetId, isDraft]);

    useEffect(() => {
        if (!executing) return undefined;
        const timer = setInterval(() => {
            services.derivedAllocationSheetService.getDerivedAllocationSheetExecutionStatus({ sheetId })
                .then(({ executionStatus, executionError }) => {
                    if (executionStatus === ExecutionStatus.COMPLETED) finish(true);
                    else if (executionStatus === ExecutionStatus.FAILED) finish(false, executionError);
                })
                .catch(() => {
                    // Checked again at the next interval; the WebSocket may still report the run.
                });
        }, EXECUTION_POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [executing, sheetId]);

    const execute = () => {
        startedSheetRef.current = sheetId;
        lastStartedSheetRef.current = sheetId;
        setExecuting(true);
        services.atpWebSocketApiService.executeDerivedAllocationSheet({
            sheetId, version: CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION,
        });
    };

    return { execute };
}
